import { useState, useMemo } from 'react';
import { UserData, WebsiteDayStats, FunnelStats, TabId } from '../types';
import { TabNav } from './TabNav';
import { KpiCards } from './KpiCards';
import { UserTable } from './UserTable';
import { SignupsChart } from './charts/SignupsChart';
import { UserTypeChart } from './charts/UserTypeChart';
import { SubscriptionChart } from './charts/SubscriptionChart';
import { ActiveUsersChart } from './charts/ActiveUsersChart';
import { FeatureUsageChart } from './charts/FeatureUsageChart';
import { EngagementChart } from './charts/EngagementChart';
import { ConversionFunnel } from './charts/ConversionFunnel';
import { RevenueChart } from './charts/RevenueChart';
import { BillingChart } from './charts/BillingChart';
import { RegionalChart } from './charts/RegionalChart';
import { WebsiteTrafficChart } from './charts/WebsiteTrafficChart';
import {
  computeMRR, computeActiveUsers, computeConversionRate, computeChurn,
  computeAvgMessagesPerUser, computeOnboardingRate, computeARPU,
  formatCurrency, formatPct,
} from '../lib/metrics';

interface DashboardProps {
  users: UserData[];
  websiteStats: WebsiteDayStats[];
  funnelStats: FunnelStats;
  loading: boolean;
  error: string | null;
  lastRefresh: Date | null;
  onRefresh: () => void;
  onLogout: () => void;
  onUserUpdate: (email: string, updates: Partial<UserData>) => Promise<void>;
  onUserDelete: (email: string) => Promise<void>;
}

export function Dashboard({ users, websiteStats, funnelStats, loading, error, lastRefresh, onRefresh, onLogout, onUserUpdate, onUserDelete }: DashboardProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Exclude affiliates from all chart/KPI calculations
  const realUsers = useMemo(() => users.filter(u => !u.affiliate), [users]);

  const mrr = useMemo(() => computeMRR(realUsers), [realUsers]);
  const active7d = useMemo(() => computeActiveUsers(realUsers, 7), [realUsers]);
  const active1d = useMemo(() => computeActiveUsers(realUsers, 1), [realUsers]);
  const active30d = useMemo(() => computeActiveUsers(realUsers, 30), [realUsers]);
  const subscribedCount = useMemo(() => realUsers.filter(u => u.subscribed).length, [realUsers]);
  const totalMessages = useMemo(() => realUsers.reduce((sum, u) => sum + (u.messageCount || 0), 0), [realUsers]);
  const totalDiscoveries = useMemo(() => realUsers.reduce((sum, u) => sum + (u.similarCasesCount || 0), 0), [realUsers]);
  const conversionRate = useMemo(() => computeConversionRate(realUsers), [realUsers]);
  const churnCount = useMemo(() => computeChurn(realUsers), [realUsers]);
  const avgMsgs = useMemo(() => computeAvgMessagesPerUser(realUsers), [realUsers]);
  const onboardingRate = useMemo(() => computeOnboardingRate(realUsers), [realUsers]);
  const arpu = useMemo(() => computeARPU(realUsers), [realUsers]);

  const affiliateCount = useMemo(() => users.filter(u => u.affiliate).length, [users]);
  const websiteViewsMonth = useMemo(() => websiteStats.reduce((s, d) => s + d.total, 0), [websiteStats]);

  return (
    <div className="dashboard">
      <header className="dashboard-topbar">
        <h1>Pinpoint Admin</h1>
        <TabNav activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="topbar-actions">
          {lastRefresh && (
            <span className="last-refresh">
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button onClick={onRefresh} disabled={loading} className="refresh-btn">
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button onClick={onLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>

      {error && <div className="error-banner">{error}</div>}

      <div className="tab-content">
        {activeTab === 'overview' && (
          <>
            <KpiCards cards={[
              { label: 'Total Users', value: realUsers.length },
              { label: 'Active (7d)', value: active7d },
              { label: 'Subscribed', value: subscribedCount },
              { label: 'MRR', value: formatCurrency(mrr) },
              { label: 'Total Messages', value: totalMessages.toLocaleString() },
              { label: 'Total Discoveries', value: totalDiscoveries.toLocaleString() },
              { label: 'Website Views (30d)', value: websiteViewsMonth.toLocaleString() },
            ]} />
            <div className="charts-grid">
              <SignupsChart users={realUsers} />
              <ActiveUsersChart users={realUsers} fullWidth={false} />
              <WebsiteTrafficChart stats={websiteStats} />
              <FeatureUsageChart users={realUsers} fullWidth={false} />
              <UserTypeChart users={realUsers} />
              <RegionalChart users={realUsers} />
            </div>
          </>
        )}

        {activeTab === 'engagement' && (
          <>
            <KpiCards cards={[
              { label: 'DAU', value: active1d },
              { label: 'WAU', value: active7d },
              { label: 'MAU', value: active30d },
              { label: 'Avg Msgs/User', value: avgMsgs.toFixed(1) },
              { label: 'Onboarding Rate', value: formatPct(onboardingRate) },
            ]} />
            <div className="charts-grid">
              <ActiveUsersChart users={realUsers} fullWidth={false} />
              <FeatureUsageChart users={realUsers} fullWidth={false} />
              <EngagementChart users={realUsers} />
              <ConversionFunnel users={realUsers} funnelStats={{ installs: funnelStats.installs - affiliateCount, opens: funnelStats.opens - affiliateCount }} />
            </div>
          </>
        )}

        {activeTab === 'revenue' && (
          <>
            <KpiCards cards={[
              { label: 'MRR', value: formatCurrency(mrr) },
              { label: 'ARR', value: formatCurrency(mrr * 12) },
              { label: 'ARPU', value: formatCurrency(arpu), subtitle: 'per subscriber/mo' },
              { label: 'Conversion Rate', value: formatPct(conversionRate) },
              { label: 'Churned', value: churnCount },
            ]} />
            <div className="charts-grid">
              <SubscriptionChart users={realUsers} />
              <BillingChart users={realUsers} />
              <RevenueChart users={realUsers} />
            </div>
          </>
        )}

        {activeTab === 'users' && (
          <UserTable users={users} onUserUpdate={onUserUpdate} onUserDelete={onUserDelete} />
        )}

      </div>
    </div>
  );
}
